import React, { createContext, FC, PropsWithChildren, useContext, useRef, useState } from 'react';

import { CheckPaymentDef } from 'Src/feature/check/models';
import { GiftCardBalance, VoidDebitGiftCardRequest } from 'Src/feature/gift-card/giftCardService';
import { uniqueIntFromDateMilli } from 'Src/utils/number';

type PaymentFlowDoOptions =
  | ['cash-pay', { payment: CheckPaymentDef; amount: number; }]
  | ['cc-manual-pay', { payment: { id: CheckPaymentDef['id'] }; }]
  | ['cc-pay', { payment: CheckPaymentDef; }]
  | ['gc-account-number', string | undefined]
  | ['gc-balance', GiftCardBalance | undefined]
  | ['gc-lookup-end']
  | ['gc-lookup-start']
  | ['gc-pay', { checkId: number; payment: CheckPaymentDef; }]
  | ['gc-payment-method-id', number | undefined]
  | ['gc-transaction-id']
  | ['other-pay', { payment: CheckPaymentDef; }]
  | ['preAuth-adjust', { payment: CheckPaymentDef; }]
  | ['part-init', amount: number]
  | ['split-init', response: number[]]
  | ['split-next']
  | ['on-complete-modal']
  ;

type PaymentFlowIntent =
  | 'cash'
  | 'cc'
  | 'cc-manual'
  | 'complete'
  | 'gc'
  | 'gc-lookup'
  | 'init'
  | 'other'
  | 'part'
  | 'preAuth'
  | 'split'
  | 'unpaid'
  ;

type PaymentFlowState = {
  intent: PaymentFlowIntent[];
  part: {
    amount: number;
  };
  split: {
    response: number[];
    count: number;
    amount: number;
  };
  cash: {
    amount: number;
    changeDue: number;
  };
  pay: {
    id: CheckPaymentDef['id'] | undefined
  };
  gc: {
    request: VoidDebitGiftCardRequest | undefined;
    balance: GiftCardBalance | undefined;
    accountNumber: string | undefined;
    paymentMethodId: number | undefined;
    transactionNumber: number;
  };
}

const initialState: PaymentFlowState = {
  intent: ['unpaid'],
  part: {
    amount: 0,
  },
  split: {
    response: [],
    count: 0,
    amount: 0,
  },
  cash: {
    amount: 0,
    changeDue: 0,
  },
  pay: {
    id: undefined,
  },
  gc: {
    request: undefined,
    balance: undefined,
    accountNumber: undefined,
    paymentMethodId: undefined,
    transactionNumber: uniqueIntFromDateMilli()
  },
};

const usePaymentFlowStore = () => {
  const [intent, intentSet] = useState<PaymentFlowState['intent']>(initialState.intent);
  const [split, splitSet] = useState<PaymentFlowState['split']>(initialState.split);
  const [part, partSet] = useState<PaymentFlowState['part']>(initialState.part);
  const [cash, cashSet] = useState<PaymentFlowState['cash']>(initialState.cash);
  const [pay, paySet] = useState<PaymentFlowState['pay']>(initialState.pay);
  const [gc, gcSet] = useState<PaymentFlowState['gc']>(initialState.gc);

  // ref each state value for stable accessors in callbacks
  const ref = useRef({
    intent,
    split,
    part,
    cash,
    pay,
    gc,
    // inform consumers that we are navigating back from flip and tip
    afterFlipAndTip: false,
    // helpers inside ref for minimizing useEffect calls
    // helpers inside ref for minimizing useEffect calls
    is: {
      get afterFlipAndTip() {
        return ref.current.afterFlipAndTip;
      },
      set afterFlipAndTip(next: boolean) {
        ref.current.afterFlipAndTip = next;
      },
      get split() {
        // if any states match the naming for even split flow
        return ref.current.intent.some((intent) => intent === 'split');
      },
      get part() {
        // if any states match the naming for partial payment flow
        return ref.current.intent.some((intent) => intent === 'part');
      },
      get gc() {
        // if any states match the naming for even split flow
        return ref.current.intent.some((intent) => intent === 'gc');
      },
      get unpaid() {
        // if any states match the naming for unpaid payment flow
        return ref.current.intent.some((intent) => intent === 'unpaid');
      },
      any: (...intents: PaymentFlowIntent[]) => intents
        .some((intent) => ref.current.intent
          .some((current) => intent === current)),
      every: (...intents: PaymentFlowIntent[]) => intents
        .every((intent) => ref.current.intent
          .some((current) => intent === current)),
    },
    has: {
      get split() {
        if (!ref.current.is.split) return false;
        if (ref.current.split.count === 0) return false;
        if (ref.current.split.response.length === 0) return false;
        return ref.current.split.count <= ref.current.split.response.length;
      }
    },
  });

  // sort & de-dupe
  const intent_set = (...args: PaymentFlowIntent[]) => {
    ref.current.intent = [...new Set([...args])].sort();
    intentSet(ref.current.intent);
  };

  const intent_remove = (...args: PaymentFlowIntent[]) => {
    const prev = [...ref.current.intent];
    const next = prev.filter((intent) => !args.includes(intent));
    intent_set(...next);
  };

  const intent_append = (...args: PaymentFlowIntent[]) => {
    intent_set(...ref.current.intent, ...args);
  };

  const patch_split = (patch: Partial<PaymentFlowState['split']>) => {
    ref.current.split = { ...ref.current.split, ...patch };
    splitSet(ref.current.split);
  };

  const patch_part = (patch: Partial<PaymentFlowState['part']>) => {
    ref.current.part = { ...ref.current.part, ...patch };
    partSet(ref.current.part);
  };

  const patch_cash = (patch: Partial<PaymentFlowState['cash']>) => {
    ref.current.cash = { ...ref.current.cash, ...patch };
    cashSet(ref.current.cash);
  };

  const patch_pay = (patch: Partial<PaymentFlowState['pay']>) => {
    ref.current.pay = { ...ref.current.pay, ...patch };
    paySet(ref.current.pay);
  };

  const patch_giftCard = (patch: Partial<PaymentFlowState['gc']>) => {
    ref.current.gc = { ...ref.current.gc, ...patch };
    gcSet(ref.current.gc);
  };

  // "unpaid", "split", "init", "gc-lookup", & "part" should not be reset as these need to persist in "some" cases
  const intent_reset_to = (intent: Exclude<PaymentFlowIntent, 'unpaid' | 'split' | 'part' | 'init' | 'gc-lookup'>) => {
    // can only have singular intents so lets wipe the slate clear of all of them
    const singles: (typeof intent)[] = [
      'cash',
      'cc',
      'cc-manual',
      'complete',
      'gc',
      'other',
      'preAuth'
    ];
    intent_remove(...singles);

    intent_append(intent);
    switch (intent) {
      case 'cash': {
        patch_pay(initialState.pay);
        patch_giftCard(initialState.gc);
        break;
      }
      case 'cc': {
        patch_cash(initialState.cash);
        patch_giftCard(initialState.gc);
        break;
      }
      case 'cc-manual': {
        patch_pay(initialState.pay);
        patch_cash(initialState.cash);
        patch_giftCard(initialState.gc);
        break;
      }
      case 'gc': {
        patch_cash(initialState.cash);
        break;
      }
      case 'other': {
        patch_pay(initialState.pay);
        patch_cash(initialState.cash);
        patch_giftCard(initialState.gc);
        break;
      }
      case 'complete': {
        intent_remove(
          'cash',
          'cc',
          'cc-manual',
          'gc',
          'gc-lookup',
          'init',
          'other',
          'part',
        );
        intent_append(intent);
        if (ref.current.is.split) {
          // !!! DO NOT RESET SPLIT FLOW as that may need to persist
          // - all others should be good to go
          patch_part(initialState.part);
          patch_cash(initialState.cash);
          patch_pay(initialState.pay);
          patch_giftCard(initialState.gc);
        } else {
          reset();
        }
        break;
      }
    }
  };

  const reset = () => {
    intent_set(...initialState.intent);
    patch_split(initialState.split);
    patch_part(initialState.part);
    patch_cash(initialState.cash);
    patch_pay(initialState.pay);
    patch_giftCard(initialState.gc);
    ref.current.afterFlipAndTip = false;
  };

  const afterFlipAndTip = () => {
    if (ref.current.has.split) {
      do_flow('split-next');
    } else {
      // reset the payment flow for any other intent other than "payment-split"
      // - "payment-split" must persist state to track current steps of payment split process
      reset();
    }
    // !!! must be done last as we have to append on to ref value after a possible "reset"
    // inform consumers that we are navigating back from flip and tip
    ref.current.afterFlipAndTip = true;
  };

  const do_flow = (...args: PaymentFlowDoOptions) => {
    const [intent] = args;
    switch (intent) {
      case 'split-init': {
        const [_, response] = args;
        intent_set('split', 'init');
        patch_split({
          response,
          amount: response[0],
          count: 1,
        });
        break;
      }
      case 'split-next': {
        intent_set('split');
        const { split } = ref.current;
        const countCurrent = split.count;
        const countNext = countCurrent + 1;
        if (countNext <= split.response.length) {
          patch_split({
            count: countNext,
            amount: split.response[countCurrent]
          });
        } else {
          patch_split(initialState.split);
          intent_set(...initialState.intent);
          ref.current.afterFlipAndTip = false;
        }
        break;
      }
      case 'part-init': {
        const [_, amount] = args;
        intent_set('part', 'init');
        patch_part({ amount });
        break;
      }
      case 'cash-pay': {
        const [_, { payment, amount }] = args;
        intent_reset_to('cash');
        patch_cash({
          changeDue: payment.change ?? 0,
          amount,
        });
        patch_pay({ id: payment.id });
        break;
      }
      case 'other-pay': {
        const [_, { payment }] = args;
        intent_reset_to('other');
        patch_pay({ id: payment.id });
        break;
      }
      case 'preAuth-adjust': {
        const [_, { payment }] = args;
        intent_reset_to('preAuth');
        patch_pay({ id: payment.id });
        break;
      }
      case 'cc-pay': {
        const [_, { payment }] = args;
        intent_reset_to('cc');
        patch_pay({ id: payment.id });
        break;
      }
      case 'cc-manual-pay': {
        const [_, { payment }] = args;
        intent_reset_to('cc-manual');
        patch_pay({ id: payment.id });
        break;
      }
      case 'gc-lookup-start': {
        intent_reset_to('gc');
        intent_append('gc-lookup');
        break;
      }
      case 'gc-lookup-end': {
        intent_remove('gc-lookup');
        break;
      }
      case 'gc-pay': {
        const [_, { checkId, payment }] = args;
        intent_reset_to('gc');
        patch_pay({ id: payment.id });
        patch_giftCard({
          request: {
            authGuid: payment.giftCardData?.authGuid ?? '',
            checkId,
            paymentId: payment.id,
            transactionNumber: uniqueIntFromDateMilli(),
          },
        });
        break;
      }
      case 'gc-balance': {
        const [_, balance] = args;
        intent_reset_to('gc');
        patch_giftCard({ balance });
        break;
      }
      case 'gc-account-number': {
        const [_, accountNumber] = args;
        intent_reset_to('gc');
        patch_giftCard({ accountNumber });
        break;
      }
      case 'gc-payment-method-id': {
        const [_, paymentMethodId] = args;
        intent_reset_to('gc');
        patch_giftCard({ paymentMethodId });
        break;
      }
      case 'gc-transaction-id': {
        intent_reset_to('gc');
        patch_giftCard({ transactionNumber: uniqueIntFromDateMilli() });
        break;
      }
      case 'on-complete-modal': {
        intent_reset_to('complete');
        break;
      }
    }
  };

  return {
    // state change actions
    do: do_flow,
    afterFlipAndTip,
    reset,
    // stateful values
    get is() { return ref.current.is; },
    get has() { return ref.current.has; },
    get intent() { return ref.current.intent; },
    get split() { return ref.current.split; },
    get part() { return ref.current.part; },
    get cash() { return ref.current.cash; },
    get pay() { return ref.current.pay; },
    get gc() { return ref.current.gc; },
    // The "basis" is so that we can calculate change on a partial payment.
    //  - Say you and I were splitting a $25 check, so we each owe $12.50.
    //  - If I give $15 as cash payment, I should get $2.50 back, and if you gave a $20 bill as cash payment you should get $7.25 back.
    get basis() {
      if (ref.current.is.split) return ref.current.split.amount;
      if (ref.current.is.part) return ref.current.part.amount;
      return undefined;
    },
  };
};

type PaymentFlowContextType = ReturnType<typeof usePaymentFlowStore>;

const PaymentFlowContext = createContext(undefined as unknown as PaymentFlowContextType);

const PaymentFlowProvider: FC<PropsWithChildren> = ({ children }) => {
  const store = usePaymentFlowStore();
  return (
    <PaymentFlowContext.Provider value={store}>
      {children}
    </PaymentFlowContext.Provider>
  );
};

const usePaymentFlow = () => {
  const store = useContext(PaymentFlowContext);
  if (!store) throw new Error('usePaymentFlow should be used within PaymentFlowProvider');
  return store;
};

type UsedPaymentFlow = ReturnType<typeof usePaymentFlow>;

export { PaymentFlowProvider, usePaymentFlow };
export type { PaymentFlowIntent, UsedPaymentFlow };
